// Browser detection
const isBrowser =
   typeof window !== 'undefined' && typeof window.document !== 'undefined'

// Declare performance variable
let performance

// Use browser's performance API if available, otherwise import from Node.js
if (isBrowser) {
   performance = window.performance
} else {
   // Dynamic import for Node.js environment
   const loadPerformance = async () => {
      const perfHooks = await import('perf_hooks')
      performance = perfHooks.performance
   }

   // Execute the async function immediately
   loadPerformance()
}
import {
   calculateMonthlyProductCosts,
   getBasePrice,
   applyMonthlyDiscounts,
   applyOneTimeDiscounts,
   MonthlyPrice,
   OneTimePrice,
   calculateMRRTotal,
   setUSDValuesOnPriceObject,
} from './calculate.mjs'
import { ProductInputType } from './types/types.ts'
import { isAlwaysUSD } from './helpers.mjs'
import {
   createEnhancedSecureExecutor,
   quickValidate,
} from './enhancedSecureExecutor.mjs'
import { securityMonitor } from './securityMonitor.mjs'
// DO NOT IMPORT ANYTHING ELSE EVER!!

//TODO: proration doesn't work yet, and I'm not 100% sure on boolean or Decimal products, or Onetime dynamic products, but I've tried to implement what I can
/**
 * Calculates the total monthly and one time costs of all dynamic products.
 *
 * @param {Record<string, any>} product_list - The list of products.
 * @param {Record<string, any>} discount_list - The list of discounts.
 * @param {Record<string, any>} discountStates - The list of discount states, where the keys are discount codes and the values are boolean values indicating whether the discount is on or off.
 * @param {Record<string, any>} promotionalDiscounts - The list of promotional discounts, where the keys are discount codes and the values are boolean values indicating whether the discount is on or off.
 * @param {CurrencyObject} currencyObject - The currency object.
 * @param {Map<string, number>} dynamic_products - The list of dynamic products, with their codes as keys and values as numbers.
 * @param {string} quote_type - The type of quote, either 'monthly' or 'onetime'.
 * @param {string} user_role - The role of the user, either 'admin' or 'user'.
 * @param {object} allPermissions - The featurepermissions.
 * @param {Record<string, number>} headcountMap - A map of non-dynamic product codes to their current headcounts.
 * @returns {object} An object with three properties, dynamicProductCosts, dynamicMonthlyTotal and dynamicOneTimeTotal, which are of type MonthlyPrice and OneTimePrice respectively.
 */
export function calculateDynamicProducts(
   product_list,
   discount_list,
   discountStates,
   promotionalDiscounts,
   currencyObject,
   pricebookExchangeRate,
   dynamic_products,
   quote_type,
   user_role,
   allPermissions,
   headcountMap = {},
) {
   let dynamicMonthlyTotal = new MonthlyPrice()
   let dynamicOneTimeTotal = new OneTimePrice()
   let alwaysUSDMonthlyTotal = new MonthlyPrice()
   let alwaysUSDOneTimeTotal = new OneTimePrice()
   let dynamicProductCosts = new Map()
   const pricebookRate = pricebookExchangeRate ?? 1

   for (const [productCode, value] of dynamic_products?.entries() ?? []) {
      if (!productCode || !product_list[productCode]) {
         continue
      }
      // Time attendance is a first-class time product handled by calculateQuote directly,
      // not a dynamic product. Skip it here to avoid double counting.
      if (productCode === 'timeAttendance') {
         continue
      }
      const product = product_list[productCode]
      const productBillingType = product.BillingFreq
      const productInputType = product.InputType
      let dynamicProductCost
      //TODO: fully test and flesh out for boolean or decimal products
      if (productInputType === ProductInputType.Boolean) {
         const resolvedHeadcount = resolveHeadcount(product, headcountMap)
         if (resolvedHeadcount !== null && product.IsPEPM && value > 0) {
            // Boolean product with UseHeadcountOf and IsPEPM: calculate as PEPM using resolved headcount
            if (productBillingType === 'once') {
               dynamicProductCost = calculateOneTimeProductCosts(
                  product_list,
                  productCode,
                  resolvedHeadcount,
               )
            } else {
               dynamicProductCost = calculateMonthlyProductCosts(
                  resolvedHeadcount,
                  product.EmpListPrice,
                  product.EmpListPrice,
                  product.MinimumListPrice,
               )
            }
         } else {
            dynamicProductCost = calculateBooleanProduct(
               product_list,
               productCode,
               value,
            )
         }
      } else if (productInputType === ProductInputType.Integer) {
         dynamicProductCost = calculateIntegerProduct(
            product_list,
            productCode,
            value,
         )
      } else if (productInputType === ProductInputType.Decimal) {
         dynamicProductCost = calculateDecimalProduct(
            product_list,
            productCode,
            value,
         )
      }

      // Skip processing if calculation failed
      if (
         !dynamicProductCost ||
         dynamicProductCost === null ||
         dynamicProductCost === undefined
      ) {
         console.warn(`Failed to calculate product cost for ${productCode}`)
         continue
      }

      dynamicProductCost.SFProduct2Id = product.SFProduct2Id
      dynamicProductCost.SFPricebookEntryID = product.SFPricebookEntryID
      dynamicProductCost.productCode = productCode
      if (productBillingType === 'monthly' && quote_type !== 'onetime') {
         applyMonthlyDiscounts(
            discount_list,
            discountStates,
            productCode,
            dynamicProductCost,
            0,
            0,
            0,
            discount_list['bundleDiscount']['DiscountAmount'],
            promotionalDiscounts,
            currencyObject,
            user_role,
            allPermissions,
         )
         dynamicProductCost.MRRTotal = calculateMRRTotal(dynamicProductCost)
         dynamicProductCost.listMRRTotal = dynamicProductCost.MRRTotal
         setUSDValuesOnPriceObject(
            dynamicProductCost,
            currencyObject?.Rate,
            pricebookRate,
         )
         if (product.MRRFunction && product.MRRFunction.trim() !== '') {
            // Only measure and report performance in test and dev environments
            const isDevelopmentOrTest =
               typeof process !== 'undefined' &&
               process.env &&
               (process.env.REACT_APP_ENVIRONMENT === 'test' ||
                  process.env.REACT_APP_ENVIRONMENT === 'dev' ||
                  process.env.NODE_ENV === 'test' ||
                  process.env.NODE_ENV === 'development')

            let startTime
            if (isDevelopmentOrTest) {
               startTime = performance.now()
            }

            try {
               const secureExecutor = createEnhancedSecureExecutor()
               const result = secureExecutor.executeFunction(
                  product.MRRFunction,
                  [
                     dynamicProductCost,
                     product_list,
                     pricebookRate,
                     currencyObject,
                  ],
                  dynamicProductCost,
               )

               // Only log performance metrics in development or test environments
               if (isDevelopmentOrTest) {
                  const executionTime = performance.now() - startTime
                  securityMonitor.logExecution(
                     productCode,
                     product.MRRFunction,
                     true,
                     null,
                     executionTime,
                  )
               }
            } catch (error) {
               // Always log security issues regardless of environment
               securityMonitor.logSuspiciousAttempt(
                  productCode,
                  product.MRRFunction,
                  error.message,
               )
               // Continue processing other products even if one fails
            }
         }
         dynamicProductCost.listMRRTotal = dynamicProductCost.MRRTotal
         //TODO: Add difference quoting?
         dynamicProductCosts.set(productCode, dynamicProductCost)
         // Determine which total to add to: alwaysUSD products go to payroll totals
         const monthlyTarget = isAlwaysUSD(productCode) ? alwaysUSDMonthlyTotal : dynamicMonthlyTotal
         if (product?.IsPEPM) {
            monthlyTarget.listPEPMCost += dynamicProductCost.listPEPMCost
            monthlyTarget.discountedPEPMCost +=
               dynamicProductCost.discountedPEPMCost
            monthlyTarget.pdfDisplayDiscountedPEPMCost +=
               dynamicProductCost.pdfDisplayDiscountedPEPMCost
            monthlyTarget.totalPEPMDiscount +=
               dynamicProductCost.totalPEPMDiscount
         }
         monthlyTarget.listPrice += dynamicProductCost.listPrice
         monthlyTarget.totalDiscount += dynamicProductCost.totalDiscount
         monthlyTarget.pdfDisplayTotalDiscount += dynamicProductCost.pdfDisplayTotalDiscount
         monthlyTarget.totalPrice += dynamicProductCost.totalPrice
         monthlyTarget.pdfDisplayTotal += dynamicProductCost.pdfDisplayTotal
         monthlyTarget.MRRTotal += dynamicProductCost.MRRTotal
         monthlyTarget.listMRRTotal += dynamicProductCost.listMRRTotal
         monthlyTarget.previousMRRTotal +=
            dynamicProductCost.previousMRRTotal
         monthlyTarget.previousTotalPrice +=
            dynamicProductCost.previousTotalPrice
         monthlyTarget.previousTotalPriceUnconverted +=
            dynamicProductCost.previousTotalPriceUnconverted
         monthlyTarget.totalPriceDifference +=
            dynamicProductCost.totalPriceDifference
         monthlyTarget.pdfDisplayDifferenceTotal +=
            dynamicProductCost.totalPriceDifference
         monthlyTarget.appliedDiscounts.push(
            ...dynamicProductCost.appliedDiscounts,
         )
         monthlyTarget.proratedTotal += dynamicProductCost.proratedTotal
         monthlyTarget.proratedPrice += dynamicProductCost.proratedPrice
         monthlyTarget.listPriceUSD += dynamicProductCost.listPriceUSD
         monthlyTarget.totalPriceUSD += dynamicProductCost.totalPriceUSD
         monthlyTarget.totalDiscountUSD +=
            dynamicProductCost.totalDiscountUSD
         monthlyTarget.MRRTotalUSD += dynamicProductCost.MRRTotalUSD
      } else if (productBillingType === 'once' && quote_type !== 'monthly') {
         applyOneTimeDiscounts(
            discount_list,
            discountStates,
            productCode,
            dynamicProductCost,
            false,
            promotionalDiscounts,
            currencyObject,
         )
         setUSDValuesOnPriceObject(
            dynamicProductCost,
            currencyObject?.Rate,
            pricebookRate,
         )
         dynamicProductCosts.set(productCode, dynamicProductCost)
         // Determine which total to add to: alwaysUSD products go to payroll totals
         const onetimeTarget = isAlwaysUSD(productCode) ? alwaysUSDOneTimeTotal : dynamicOneTimeTotal
         onetimeTarget.listPrice += dynamicProductCost.listPrice
         onetimeTarget.totalDiscount += dynamicProductCost.totalDiscount
         onetimeTarget.totalPrice += dynamicProductCost.totalPrice
         onetimeTarget.pdfDisplayTotal += dynamicProductCost.pdfDisplayTotal
         onetimeTarget.previousTotalPrice +=
            dynamicProductCost.previousTotalPrice
         onetimeTarget.previousTotalPriceUnconverted +=
            dynamicProductCost.previousTotalPriceUnconverted
         onetimeTarget.totalPriceDifference +=
            dynamicProductCost.totalPriceDifference
         onetimeTarget.appliedDiscounts.push(
            ...dynamicProductCost.appliedDiscounts,
         )
         onetimeTarget.listPriceUSD += dynamicProductCost.listPriceUSD
         onetimeTarget.totalPriceUSD += dynamicProductCost.totalPriceUSD
         onetimeTarget.totalDiscountUSD +=
            dynamicProductCost.totalDiscountUSD
      }
   }
   dynamicMonthlyTotal.totalDiscountPercent =
      ((dynamicMonthlyTotal.totalDiscount || 0) /
         (dynamicMonthlyTotal.listPrice || 1)) *
      100
   dynamicMonthlyTotal.pdfDisplayTotalDiscountPercent =
      ((dynamicMonthlyTotal.pdfDisplayTotalDiscount || 0) /
         (dynamicMonthlyTotal.listPrice || 1)) *
      100
   dynamicOneTimeTotal.totalDiscountPercent =
      ((dynamicOneTimeTotal.totalDiscount || 0) /
         (dynamicOneTimeTotal.listPrice || 1)) *
      100
   alwaysUSDMonthlyTotal.totalDiscountPercent =
      ((alwaysUSDMonthlyTotal.totalDiscount || 0) /
         (alwaysUSDMonthlyTotal.listPrice || 1)) *
      100
   alwaysUSDOneTimeTotal.totalDiscountPercent =
      ((alwaysUSDOneTimeTotal.totalDiscount || 0) /
         (alwaysUSDOneTimeTotal.listPrice || 1)) *
      100
   return { dynamicProductCosts, dynamicMonthlyTotal, dynamicOneTimeTotal, alwaysUSDMonthlyTotal, alwaysUSDOneTimeTotal }
}

/**
 * Resolves the headcount for a product based on its UseHeadcountOf field.
 * Sums the headcounts of all referenced product codes from the headcountMap.
 *
 * @param {object} product - The product object.
 * @param {Record<string, number>} headcountMap - A map of non-dynamic product codes to their current headcounts.
 * @returns {number|null} The resolved headcount, or null if UseHeadcountOf is not set.
 */
function resolveHeadcount(product, headcountMap) {
   if (!product.UseHeadcountOf || product.UseHeadcountOf.size === 0) {
      return null
   }
   let total = 0
   for (const code of product.UseHeadcountOf) {
      total += (headcountMap[code] || 0)
   }
   return total
}

function calculateIntegerProduct(product_list, productCode, productCount) {
   if (!productCode || !product_list[productCode]) {
      return null
   }
   //TODO: fix all other properties, ie: productCode
   const product = product_list[productCode]
   const productBillingType = product.BillingFreq
   let dynamicProductCost
   if (productBillingType === 'monthly') {
      dynamicProductCost = calculateMonthlyProductCosts(
         productCount,
         product.EmpListPrice,
         product.EmpListPrice,
         product.MinimumListPrice,
      )
   } else if (productBillingType === 'once') {
      dynamicProductCost = calculateOneTimeProductCosts(
         product_list,
         productCode,
         productCount,
      )
   }
   return dynamicProductCost
}

function calculateDecimalProduct(product_list, productCode, productCount) {
   if (!productCode || !product_list[productCode]) {
      return null
   }
   const product = product_list[productCode]
   const productBillingType = product.BillingFreq
   let dynamicProductCost
   if (productBillingType === 'monthly') {
      dynamicProductCost = calculateMonthlyProductCosts(
         productCount,
         product.EmpListPrice,
         product.EmpListPrice,
         product.MinimumListPrice,
      )
   } else if (productBillingType === 'once') {
      dynamicProductCost = calculateOneTimeProductCosts(
         product_list,
         productCode,
         productCount,
      )
   }
   return dynamicProductCost
}

function calculateBooleanProduct(product_list, productCode, productCount) {
   if (!productCode || !product_list[productCode]) {
      return null
   }
   const product = product_list[productCode]
   const productBillingType = product.BillingFreq
   let dynamicProductCost
   if (productBillingType === 'monthly') {
      dynamicProductCost = new MonthlyPrice()
      if (productCount > 0) {
         dynamicProductCost.listPrice = product.BasePrice
         dynamicProductCost.totalPrice = product.BasePrice
         dynamicProductCost.pdfDisplayTotal = dynamicProductCost.listPrice
         dynamicProductCost.productEECount = 1
      }
   } else if (productBillingType === 'once') {
      dynamicProductCost = new OneTimePrice()
      if (productCount > 0) {
         dynamicProductCost.listPrice = product.BasePrice
         dynamicProductCost.totalPrice = product.BasePrice
         dynamicProductCost.pdfDisplayTotal = dynamicProductCost.listPrice
         dynamicProductCost.productEECount = 1
      }
   }
   return dynamicProductCost
}

// Function takes in a EE count and base PEPM pricing, and base product cost, returns JSON object with data to display
//
function calculateOneTimeProductCosts(
   product_list,
   productCode,
   productEECount,
) {
   let productCosts = new OneTimePrice()

   // If the impProductCode does not exist in the product list return 0 for all values
   if (product_list[productCode]?.NotInPricebook) {
      return productCosts
   }

   // if value greater then 0, calculate costs, else return 0 for all values
   if (productEECount <= 0) {
      return productCosts
   }

   // Determines base pricing, banded or formula based.
   let basePrice = getBasePrice(product_list, productCode, productEECount)

   // Sets total pricing and EE count
   productCosts.listPrice =
      productEECount * product_list[productCode]['EmpListPrice'] + basePrice
   productCosts.totalPrice =
      productEECount * product_list[productCode]['EmpListPrice'] + basePrice
   productCosts.pdfDisplayTotal = productCosts.listPrice
   productCosts.productEECount = productEECount

   return productCosts
}
